import {
  Inject,
  Injectable,
  InjectionToken,
  Injector,
  NgZone,
  OnDestroy,
  Optional
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { marker as _ } from '@bling-fe/shared/utils';

// Friendly labels for bare internal URLs, keyed by the first path segment.
// Only applied when the anchor text is the URL itself (autolinked paste) —
// links with author-written text are never rewritten.
const ROUTE_LABEL_KEYS: Record<string, string> = {
  home: _('COMMON.INTERNAL_LINK_LABELS.HOME'),
  post: _('COMMON.INTERNAL_LINK_LABELS.POST'),
  event: _('COMMON.INTERNAL_LINK_LABELS.EVENT'),
  calendar: _('COMMON.INTERNAL_LINK_LABELS.CALENDAR'),
  groups: _('COMMON.INTERNAL_LINK_LABELS.GROUP'),
  group: _('COMMON.INTERNAL_LINK_LABELS.GROUP'),
  'my-tenant': _('COMMON.INTERNAL_LINK_LABELS.SERVICES'),
  community: _('COMMON.INTERNAL_LINK_LABELS.COMMUNITY'),
  ticket: _('COMMON.INTERNAL_LINK_LABELS.TICKET'),
  'repair-messages': _('COMMON.INTERNAL_LINK_LABELS.TICKET'),
  marketplace: _('COMMON.INTERNAL_LINK_LABELS.MARKETPLACE'),
  members: _('COMMON.INTERNAL_LINK_LABELS.MEMBERS'),
  profile: _('COMMON.INTERNAL_LINK_LABELS.PROFILE'),
  'daily-reservation': _('COMMON.INTERNAL_LINK_LABELS.RESERVATION'),
  'hourly-reservation': _('COMMON.INTERNAL_LINK_LABELS.RESERVATION'),
  'room-booking': _('COMMON.INTERNAL_LINK_LABELS.RESERVATION'),
  services: _('COMMON.INTERNAL_LINK_LABELS.SERVICES'),
  documents: _('COMMON.INTERNAL_LINK_LABELS.DOCUMENTS')
};

// Internal routes whose first id segment identifies a resource that can be
// resolved to a real name (group title, post/event title). Maps the route
// segment to a stable resource type used by ResolveLinkTitlesDirective.
export type ResourceLinkType = 'group' | 'post' | 'event' | 'document';

// An href can only name an in-app route when it is absolute (has a scheme) or
// root-relative. Anything else is text the author typed without a scheme —
// "name@firma.ch", "www.example.ch" — and resolving it against the current page
// would turn it into a bogus internal link: relabelled with the section name of
// whatever route the reader happens to be on, and swallowed by the click
// interceptor.
const ROUTABLE_HREF = /^([a-z][a-z0-9+.-]*:|\/)/i;

// A scheme-less href that is nothing but an e-mail address — what the admin
// editor stores when an author types a bare address into its link dialog. The
// ":" exclusion keeps anything carrying a scheme out of this branch.
const BARE_EMAIL = /^[^\s@/?#:]+@[^\s@/?#:]+\.[^\s@/?#:]+$/;

const RESOURCE_LINK_TYPES: Record<string, ResourceLinkType> = {
  group: 'group',
  groups: 'group',
  post: 'post',
  event: 'event',
  documents: 'document'
};

/**
 * Resolves the hosts that count as "this app" beyond window.location.host —
 * on native builds the WebView origin is a local capacitor host, so the
 * current tenant's real domains (e.g. abz.flink.coop, wink.abz.ch) must be
 * supplied by the app. See `internalLinkHostsProvider` in the shared/core lib.
 */
export const INTERNAL_LINK_HOSTS_RESOLVER = new InjectionToken<() => string[]>(
  'INTERNAL_LINK_HOSTS_RESOLVER'
);

/**
 * Optional app-provided handler for internal resource links whose click
 * should do something richer than routing — e.g. a document link opens its
 * preview/download in place instead of navigating. Returns true when the
 * click was handled.
 */
export const INTERNAL_LINK_RESOURCE_OPENER = new InjectionToken<
  (ref: { type: ResourceLinkType; id: number }) => boolean
>('INTERNAL_LINK_RESOURCE_OPENER');

/**
 * Makes links in user/admin-authored content behave correctly:
 *
 * - `normalizeLinks` classifies every anchor in an HTML string as internal
 *   (same tenant) or external, so internal links no longer carry
 *   `target="_blank"` and each kind can be styled distinctly.
 * - `init` installs a document-level click interceptor that routes internal
 *   links through the Angular router. Without it, absolute URLs trigger a
 *   full page load on web and escalate to the system browser on native
 *   (which then re-enters the app via universal links and loses the path).
 */
@Injectable({ providedIn: 'root' })
export class InternalLinkService implements OnDestroy {
  private listening = false;

  private translateResolved = false;
  private translateService: TranslateService | null = null;

  constructor(
    private router: Router,
    private zone: NgZone,
    private injector: Injector,
    @Optional()
    @Inject(INTERNAL_LINK_HOSTS_RESOLVER)
    private hostsResolver: (() => string[]) | null,
    @Optional()
    @Inject(INTERNAL_LINK_RESOURCE_OPENER)
    private resourceOpener:
      | ((ref: { type: ResourceLinkType; id: number }) => boolean)
      | null
  ) {}

  init(): void {
    if (this.listening) return;
    this.listening = true;
    this.zone.runOutsideAngular(() => {
      document.addEventListener('click', this.handleDocumentClick);
    });
  }

  ngOnDestroy(): void {
    if (!this.listening) return;
    this.listening = false;
    document.removeEventListener('click', this.handleDocumentClick);
  }

  isInternal(url: URL): boolean {
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
    if (url.host === window.location.host) return true;
    return (this.hostsResolver?.() ?? []).includes(url.host);
  }

  normalizeLinks(html: string | null | undefined): string {
    if (!html) return '';

    const template = document.createElement('template');
    template.innerHTML = html;

    template.content.querySelectorAll('a[href]').forEach(anchor => {
      const href = anchor.getAttribute('href')?.trim() ?? '';
      // A bare address is a mail link whose author left off the scheme; make it
      // one so it opens the mail client instead of going nowhere.
      if (BARE_EMAIL.test(href)) anchor.setAttribute('href', `mailto:${href}`);

      const url = this.parseHref(anchor.getAttribute('href'));
      if (!url || (url.protocol !== 'http:' && url.protocol !== 'https:')) {
        return;
      }

      if (this.isInternal(url)) {
        anchor.removeAttribute('target');
        anchor.removeAttribute('rel');
        anchor.classList.remove('external-link');
        anchor.classList.add('internal-link');
        this.applyRouteLabel(anchor, url);
      } else {
        anchor.setAttribute('target', '_blank');
        anchor.setAttribute('rel', 'noopener noreferrer');
        anchor.classList.remove('internal-link');
        anchor.classList.add('external-link');
      }
    });

    return template.innerHTML;
  }

  private handleDocumentClick = (event: MouseEvent): void => {
    if (event.defaultPrevented) return;
    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const anchor = event
      .composedPath()
      .find(
        (target): target is HTMLAnchorElement =>
          target instanceof HTMLAnchorElement
      );
    if (!anchor || anchor.hasAttribute('download')) return;

    // Inside a rich-text/markdown editor the link is being edited, not
    // followed — let the editor handle the click instead of navigating away.
    if (
      anchor.isContentEditable ||
      anchor.closest('[contenteditable="true"], [contenteditable=""]')
    ) {
      return;
    }

    const href = anchor.getAttribute('href');
    // Fragment-only hrefs are same-page anchors / legacy toggles — leave them
    // to their own handlers.
    if (!href || href.startsWith('#')) return;

    const url = this.parseHref(href);
    if (!url || !this.isInternal(url)) return;

    const opener = this.resourceOpener;
    const ref = opener && this.resourceRefFor(url);
    if (opener && ref) {
      const handled = this.zone.run(() => opener(ref));
      if (handled) {
        event.preventDefault();
        return;
      }
    }

    event.preventDefault();
    const path = `${url.pathname}${url.search}${url.hash}`;
    this.zone.run(() => this.router.navigateByUrl(path));
  };

  private parseHref(href: string | null): URL | null {
    const trimmed = href?.trim();
    if (!trimmed || !ROUTABLE_HREF.test(trimmed)) return null;
    try {
      return new URL(trimmed, window.location.href);
    } catch {
      return null;
    }
  }

  // Replaces the visible URL of a bare pasted link with a friendly section
  // label ("Kalender" instead of https://abz.flink.coop/calendar). Anchors
  // whose text was written by the author are left untouched, and unknown or
  // untranslated routes keep the URL.
  //
  // For links that point at a specific resource (a group, post or event by id)
  // the anchor is also tagged with data-resource-type/data-resource-id. The
  // generic label is shown immediately; ResolveLinkTitlesDirective then
  // upgrades it to the resource's real name asynchronously, per viewer.
  private applyRouteLabel(anchor: Element, url: URL): void {
    if (!this.hasBareUrlText(anchor)) return;

    const label = this.routeLabelFor(url);
    if (label) anchor.textContent = label;
  }

  // The specific resource a link points at — { type, id } — for the routes
  // whose title can be resolved to a name. Section links without an id
  // (/community, /calendar, /members) return null and keep their generic label.
  resourceRefFor(url: URL): { type: ResourceLinkType; id: number } | null {
    if (!this.isInternal(url)) return null;

    const segments = url.pathname.split('/').filter(Boolean);
    const [segment, rawId] = segments;
    const type = segment && RESOURCE_LINK_TYPES[segment];
    if (!type) return null;

    const id = Number(rawId);
    if (!Number.isInteger(id) || id <= 0) return null;

    return { type, id };
  }

  // For a rendered anchor, the resource whose name may replace the link text —
  // or null to leave it alone. Returns a ref only when the link points at a
  // resolvable resource AND its current text is generic (the bare URL or the
  // route's section label), so author-written link text is never overwritten.
  // (Derived from the live href because data-* attributes don't survive HTML
  // sanitisation; the href always does.)
  upgradableResourceRef(
    anchor: HTMLAnchorElement
  ): { type: ResourceLinkType; id: number } | null {
    const href = anchor.getAttribute('href');
    const url = this.parseHref(href);
    if (!url) return null;

    const ref = this.resourceRefFor(url);
    if (!ref) return null;

    const text = anchor.textContent?.trim();
    if (!text) return null;

    const isBareUrl = this.isBareUrlText(text, href);
    const isSectionLabel = text === this.routeLabelFor(url);
    if (!isBareUrl && !isSectionLabel) return null;

    return ref;
  }

  // Translated section label for a known internal route (e.g. "Gemeinschaft"
  // for /community), or null for unknown routes / when no translation is
  // available. Used when rendering bare links so the label always reflects the
  // current tenant's (possibly overridden) translations on every view.
  routeLabelFor(url: URL): string | null {
    if (!this.isInternal(url)) return null;

    const translate = this.resolveTranslateService();
    if (!translate) return null;

    const segment = url.pathname.split('/').find(Boolean);
    const key = segment && ROUTE_LABEL_KEYS[segment];
    if (!key) return null;

    const label = translate.instant(key);
    if (!label || label === key) return null;

    return label;
  }

  // True when an internal URL maps to a known section route. The editor stores
  // such links with the bare URL as their text so the render path relabels them
  // live — keeping the label in sync with the viewer's tenant translations
  // rather than freezing it at insertion time.
  hasRouteLabel(url: URL): boolean {
    if (!this.isInternal(url)) return false;
    const segment = url.pathname.split('/').find(Boolean);
    return !!(segment && ROUTE_LABEL_KEYS[segment]);
  }

  // TranslateService is resolved lazily so the service also works in
  // contexts without TranslateModule (labels are simply skipped there).
  private resolveTranslateService(): TranslateService | null {
    if (!this.translateResolved) {
      this.translateResolved = true;
      try {
        this.translateService = this.injector.get(TranslateService);
      } catch {
        this.translateService = null;
      }
    }
    return this.translateService;
  }

  private hasBareUrlText(anchor: Element): boolean {
    return this.isBareUrlText(
      anchor.textContent?.trim(),
      anchor.getAttribute('href')
    );
  }

  private isBareUrlText(
    text: string | null | undefined,
    href: string | null | undefined
  ): boolean {
    if (!text || !href) return false;

    const bare = (value: string) =>
      value.replace(/^https?:\/\//, '').replace(/\/$/, '');
    return text === href || bare(text) === bare(href);
  }
}
